import React, { useEffect, useState } from 'react';
import {Row, Col} from 'antd';
import {useSelector} from "react-redux";
import { useTranslation } from 'react-i18next';

// Types
import {FilterType, FiltersPaginationType} from "../../types/SharedTypes";
import {CatalogParamsType} from "../../types/CatalogTypes";

// Components and Subcomponents
import TitleComponent from '../subComponents/TitleComponent';
import ProductList from "./ProductList";
import FileManagement from './FileManagement';
import VerticalFilterComponent from '../subComponents/VerticalFilterComponent';
import UploadExcelModal from '../subComponents/UploadExcelModal';

// Store
import {useAppDispatch} from '../../store/store';
import {getParams, getAll, uploadCatalogToExclude} from "../../store/redux/actions/catalogActions";
import * as catalogSelectors from "../../store/redux/selectors/catalogSelectors";
import {upload as productStoreUpload} from "../../store/redux/actions/productStoreActions";
import {upload as catalogUpload} from "../../store/redux/actions/catalogActions";

// Utils
import {useScreenSizeValidator} from "../../utils/generalUtils";


const Catalog = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [paginationData, setPaginationData] = useState<FiltersPaginationType>({
    paginationVm: {
      page: 1,
      size: 10,
    },
    filterVm: {}
  });
  const [filters, setFilters] = useState<FilterType[]>([
    { name: "supermodels", label: `${t('supermodel')}:`, value: undefined, fieldType: 'input' },
    { name: "models", label: `${t('model')}:`, value: undefined, fieldType: 'input' },
    { name: "codes", label: `${t('code')}:`, value: undefined, fieldType: 'input' },
    { name: "names", label: `${t('names')}:`, value: undefined, fieldType: 'input' },
    { name: "brands", label: `${t('brands')}:`, value: undefined, fieldType: 'select',
      items: { list: [], idIdentifier: "id", valueIdentifier: "value" }, mode: "multiple" },
    { name: "stores", label: `${t('stores')}:`, value: undefined, fieldType: 'select',
      items: { list: [], idIdentifier: "id", valueIdentifier: "value" }, mode: "multiple" },
    { name: "alliances", label: `${t('alliances')}:`, value: undefined, fieldType: 'select',
      items: { list: [], idIdentifier: "id", valueIdentifier: "value" }, mode: "multiple" },
    { name: "catalogStatuses", label: `${t('actives')}:`, value: undefined, fieldType: 'select',
      items: { list: [], idIdentifier: "id", valueIdentifier: "value" }, allowClear: true },
  ]);
  const [showUploadCatalogModal, setShowUploadCatalogModal] = useState(false);
  const [showUploadCatalogToExcludeModal, setShowUploadCatalogToExcludeModal] = useState(false);
  const [showUploadProductStoreModal, setShowUploadProductStoreModal] = useState(false);
  const catalogParams = useSelector(catalogSelectors.catalogParamsSelector)!;
  const isSmallScreen = useScreenSizeValidator('(max-width: 576px)');

  useEffect(() => {
    dispatch(getParams(paginationData));
  }, [paginationData]);

  useEffect(() => {
    let newFilters = filters;
    Object.keys(catalogParams).map(key => {
      const filterFound = newFilters.find(filter => filter.name === key)!;
      if (filterFound && Array.isArray(catalogParams[key as keyof CatalogParamsType])){
        filterFound.items!.list = catalogParams[key as keyof CatalogParamsType] as any[];
      } 
    });
    setFilters(newFilters);
  }, [catalogParams]);

  const handleChangePagination = (page: number, pageSize: number) => {
    const newPagination = paginationData;
    newPagination.paginationVm.page = page;
    newPagination.paginationVm.size = pageSize;
    setPaginationData(newPagination);
    dispatch(getAll(paginationData))
  };

  return (
    <Row>
      <Col md={14} sm={14} xs={24}>
        <TitleComponent level={3} text={t('catalog')} className={'font-semibold text-gray-900'} />
      </Col>

      <Col md={24} xs={24} className={'text-left'}>
        <VerticalFilterComponent filters={filters}
                                 paginationData={paginationData}
                                 setPaginationData={setPaginationData}
                                 additionalContent={
                                  <FileManagement systemParams={catalogParams.systemParams}
                                                  onToggleUploadCatalogModal={() => setShowUploadCatalogModal(true)}
                                                  onToggleUploadCatalogToExcludeModal={() => setShowUploadCatalogToExcludeModal(true)}
                                                  onToggleUploadProductStoreModal={() => setShowUploadProductStoreModal(true)}
                                  />}
          />
      </Col>

      <Col md={24} xs={24} className={'mt-5 p-6 border border-grey-dkt-300 rounded-lg overflow-hidden bg-white'}>
        <ProductList pagination={paginationData} onChangePagination={handleChangePagination} />
      </Col>

      {showUploadCatalogModal &&
        <UploadExcelModal title={t('loadCatalog')}
                          onToggleModal={() => setShowUploadCatalogModal(false)} 
                          upload={(formData) => dispatch(catalogUpload(formData))}
        />
      }
      {showUploadCatalogToExcludeModal &&
        <UploadExcelModal title={t('loadCatalogToExclude')}
                          onToggleModal={() => setShowUploadCatalogToExcludeModal(false)} 
                          upload={(formData) => dispatch(uploadCatalogToExclude(formData))}
        />
      }
      {showUploadProductStoreModal &&
        <UploadExcelModal title={t('loadInventory')}
                          onToggleModal={() => setShowUploadProductStoreModal(false)} 
                          upload={(formData) => dispatch(productStoreUpload(formData))}
        />
      }
    </Row>
  );
}

export default Catalog;